import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Box, Stack, Tooltip, Typography } from '@mui/material';

export interface IconWithLabelProps {
  label: string;
  subLabel?: string;
  icon?: React.ReactNode;
  avatarUrl?: string;
  avatarName?: string;
  onClick?: () => void;
  to?: string;
  maxWidth?: number;
  color?: 'primary' | 'error' | 'default';
  iconBgColor?: string;
  textColor?: string;
}

const QUICKSAND = '"Quicksand", Inter, system-ui, sans-serif';

interface AvatarColor {
  bg: string;
  text: string;
  border: string;
}

// Pastel palette — one color per starting letter (A-Z cycled), same idea as NameEmailAvatar
const AVATAR_COLORS: AvatarColor[] = [
  { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' },
  { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' },
  { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' },
  { bg: '#ccfbf1', text: '#115e59', border: '#99f6e4' },
  { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' },
  { bg: '#cffafe', text: '#155e75', border: '#a5f3fc' },
  { bg: '#d1fae5', text: '#065f46', border: '#a7f3d0' },
  { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe' },
  { bg: '#ffe4e6', text: '#9f1239', border: '#fecdd3' },
  { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
];

const getAvatarColor = (name: string): AvatarColor => {
  const firstLetter = name.trim().charAt(0).toUpperCase();
  if (!firstLetter) return AVATAR_COLORS[0];
  const index = Math.max(0, (firstLetter.charCodeAt(0) - 65) % AVATAR_COLORS.length);
  return AVATAR_COLORS[index];
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const isValidImage = (url?: string): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed === '' || trimmed.includes('undefined') || trimmed.includes('null')) return false;
  return true;
};

export const IconWithLabel = ({
  label,
  subLabel,
  icon,
  avatarUrl,
  avatarName,
  onClick,
  to,
  maxWidth = 220,
  color = 'primary',
  iconBgColor,
  textColor,
}: IconWithLabelProps) => {
  const [imgFailed, setImgFailed] = useState<boolean>(false);
  const displayLabel = label || '—';
  const nameForAvatar = avatarName ?? label;
  const showAvatar = Boolean(avatarUrl || avatarName);
  const showImage = showAvatar && isValidImage(avatarUrl) && !imgFailed;
  const isClickable = Boolean(to || typeof onClick === 'function');
  const colorScheme = getAvatarColor(nameForAvatar);

  const handleClick = (event: React.MouseEvent) => {
    if (!isClickable) return;
    event.stopPropagation();
    onClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onClick?.();
    }
  };

  const isErrorColor = color === 'error';
  const resolvedBgColor = iconBgColor || (isErrorColor ? 'error.main' : color === 'default' ? 'grey.500' : 'primary.main');
  const resolvedIconColor = isErrorColor ? 'error.contrastText' : 'primary.contrastText';
  const resolvedTextColor = textColor || (isClickable ? (isErrorColor ? 'error.main' : 'primary.main') : 'text.primary');
  const resolvedHoverTextColor = textColor || (isErrorColor ? 'error.dark' : 'primary.dark');

  const content = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      justifyContent="flex-start"
      sx={{ minWidth: 0, textAlign: 'left' }}
    >
      {showAvatar ? (
        <Avatar
          src={showImage ? avatarUrl : undefined}
          imgProps={{ onError: () => setImgFailed(true) }}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.8rem',
            fontWeight: 700,
            fontFamily: QUICKSAND,
            flexShrink: 0,
            bgcolor: colorScheme.bg,
            color: colorScheme.text,
            border: `2px solid ${colorScheme.border}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          {getInitials(nameForAvatar)}
        </Avatar>
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: resolvedBgColor,
            color: resolvedIconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { width: 16, height: 16 },
          }}
        >
          {icon}
        </Box>
      )}
      <Stack spacing={0} sx={{ minWidth: 0, justifyContent: 'center', alignItems: 'flex-start', textAlign: 'left' }}>
        <Tooltip title={displayLabel} arrow placement="top">
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            align="left"
            onClick={!to && isClickable ? handleClick : undefined}
            onKeyDown={!to && isClickable ? handleKeyDown : undefined}
            tabIndex={!to && isClickable ? 0 : undefined}
            role={!to && isClickable ? 'link' : undefined}
            aria-label={isClickable ? `Open ${displayLabel}` : undefined}
            sx={{
              maxWidth,
              fontFamily: QUICKSAND,
              lineHeight: 1.35,
              color: resolvedTextColor,
              cursor: isClickable ? 'pointer' : 'default',
              textAlign: 'left',
              '&:hover': isClickable ? { textDecoration: 'underline', color: resolvedHoverTextColor } : undefined,
            }}
          >
            {displayLabel}
          </Typography>
        </Tooltip>
        {subLabel ? (
          <Tooltip title={subLabel} arrow placement="top">
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              align="left"
              sx={{ maxWidth, fontFamily: QUICKSAND, lineHeight: 1.35, textAlign: 'left' }}
            >
              {subLabel}
            </Typography>
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={handleClick}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          textAlign: 'left',
          justifyContent: 'flex-start',
        }}
      >
        {content}
      </Link>
    );
  }

  return content;
};
